import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, FileText, UploadSimple, Brain, X, DownloadSimple } from "@phosphor-icons/react";
import { fmtYmd, fmtYmdTime } from "@/lib/dateFormat";
import DatePickerYmd from "@/components/DatePickerYmd";
import VehicleQrCard from "@/components/VehicleQrCard";
import ReplacementReportDialog from "@/components/ReplacementReportDialog";
import { useAuth } from "@/context/AuthContext";

const STATUSES = [
    { v: "active", l: "稼働中" },
    { v: "draft", l: "仮登録" },
    { v: "in_maintenance", l: "整備中" },
    { v: "retired", l: "廃車" },
];

export default function VehicleDetail() {
    const { id } = useParams();
    const { features } = useAuth();
    const [v, setV] = useState(null);
    const [edit, setEdit] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [history, setHistory] = useState([]);
    const [maintenance, setMaintenance] = useState([]);
    const [aiOpen, setAiOpen] = useState(false);
    const [lightbox, setLightbox] = useState(null); // { data_url, name, kind }

    const load = async () => {
        setLoading(true);
        const [v1, h1, m1] = await Promise.all([
            api.get(`/vehicles/${id}`),
            api.get(`/inspections`, { params: { vehicle_id: id } }),
            api.get(`/maintenance`, { params: { vehicle_id: id } }),
        ]);
        setV(v1.data);
        setEdit(v1.data);
        setHistory(h1.data);
        setMaintenance(m1.data);
        setLoading(false);
    };

    useEffect(() => {
        load(); // eslint-disable-next-line
    }, [id]);

    const save = async () => {
        setSaving(true);
        try {
            const payload = { ...edit };
            delete payload.id;
            delete payload.company_id;
            delete payload.attachments;
            delete payload.created_at;
            delete payload.updated_at;
            delete payload.created_by;
            await api.patch(`/vehicles/${id}`, payload);
            toast.success("保存しました");
            await load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    const uploadAttachment = async (kind, file) => {
        if (!file) return;
        const reader = new FileReader();
        reader.onload = async () => {
            try {
                await api.post(`/vehicles/${id}/attachments`, {
                    kind,
                    name: file.name,
                    data_url: reader.result,
                });
                toast.success("添付しました");
                await load();
            } catch (e) {
                toast.error(formatApiError(e.response?.data?.detail));
            }
        };
        reader.readAsDataURL(file);
    };

    const downloadReport = async () => {
        try {
            const res = await api.post(`/reports`, { kind: "vehicle_card", vehicle_id: id }, { responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            window.open(url, "_blank");
        } catch {
            toast.error("PDFの生成に失敗しました");
        }
    };

    if (loading || !v) return <p className="prc-label-overline" data-testid="vd-loading">読込中…</p>;

    return (
        <div className="space-y-5" data-testid="vehicle-detail-page">
            <div className="flex items-center gap-3">
                <Link to="/vehicles" className="prc-btn-ghost inline-flex items-center gap-1 text-sm" data-testid="vd-back"><ArrowLeft size={16} />一覧に戻る</Link>
                <button onClick={downloadReport} className="prc-btn-ghost inline-flex items-center gap-2 text-sm" data-testid="vd-pdf-btn"><FileText size={16} />車両カードPDF</button>
                {features?.paid_ai_enabled && (
                    <button onClick={() => setAiOpen(true)} className="prc-btn-primary inline-flex items-center gap-2 text-sm" data-testid="vd-ai-btn"><Brain size={16} />AI買替判断</button>
                )}
            </div>

            <div>
                <p className="prc-label-overline">車両詳細</p>
                <h1 className="text-3xl font-mono font-bold tracking-tight" data-testid="vd-plate">{v.plate_number}</h1>
                {(() => {
                    // 年式 = 初度検査年月から算出した経過年数で表示する
                    let yearLabel = "-";
                    if (v.first_inspection_date) {
                        const m = String(v.first_inspection_date).match(/^(\d{4})-(\d{2})/);
                        if (m) {
                            const y = parseInt(m[1]);
                            const mo = parseInt(m[2]);
                            const now = new Date();
                            const ageY = now.getFullYear() - y - (now.getMonth() + 1 < mo ? 1 : 0);
                            if (ageY >= 0) yearLabel = `${ageY}年`;
                        }
                    } else if (v.year) {
                        const ageY = new Date().getFullYear() - parseInt(v.year);
                        if (ageY >= 0) yearLabel = `${ageY}年`;
                    }
                    return (
                        <p className="text-sm text-[var(--prc-muted)] mt-1" data-testid="vd-year-display">
                            {v.make} {v.model} · 年式 {yearLabel}
                        </p>
                    );
                })()}
            </div>

            <div className="grid lg:grid-cols-3 gap-4">
                <div className="prc-card p-5 lg:col-span-2">
                    <p className="prc-label-overline mb-3">基本情報</p>
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="登録番号" value={edit.plate_number} onChange={(val) => setEdit({ ...edit, plate_number: val })} testid="vd-plate-input" />
                        <Field label="車台番号" value={edit.chassis_number} onChange={(val) => setEdit({ ...edit, chassis_number: val })} testid="vd-chassis-input" />
                        <Field label="メーカー" value={edit.make} onChange={(val) => setEdit({ ...edit, make: val })} testid="vd-make-input" />
                        <Field label="車種" value={edit.model} onChange={(val) => setEdit({ ...edit, model: val })} testid="vd-model-input" />
                        <Field label="初度検査年月" type="month" value={edit.first_inspection_date} onChange={(val) => setEdit({ ...edit, first_inspection_date: val, year: val ? parseInt(val.slice(0, 4)) : null })} testid="vd-first-insp-input" />
                        <div>
                            <label className="prc-label-overline block mb-1">年式 (自動計算)</label>
                            <div className="prc-input !opacity-70 !cursor-not-allowed select-none" data-testid="vd-year-readonly">
                                {(() => {
                                    const fid = edit.first_inspection_date;
                                    if (fid) {
                                        const m = String(fid).match(/^(\d{4})-(\d{2})/);
                                        if (m) {
                                            const y = parseInt(m[1]);
                                            const mo = parseInt(m[2]);
                                            const now = new Date();
                                            const age = now.getFullYear() - y - (now.getMonth() + 1 < mo ? 1 : 0);
                                            if (age >= 0) return `${age} 年`;
                                        }
                                    }
                                    if (edit.year) {
                                        const age = new Date().getFullYear() - parseInt(edit.year);
                                        if (age >= 0) return `${age} 年`;
                                    }
                                    return "—";
                                })()}
                            </div>
                        </div>
                        <Field label="登録日" type="date" value={edit.registration_date} onChange={(val) => setEdit({ ...edit, registration_date: val })} testid="vd-regdate-input" />
                        <Field label="車検満了日" type="date" value={edit.inspection_expiry} onChange={(val) => setEdit({ ...edit, inspection_expiry: val })} testid="vd-expiry-input" />
                        <Field label="保険満了日" type="date" value={edit.insurance_expiry} onChange={(val) => setEdit({ ...edit, insurance_expiry: val })} testid="vd-ins-input" />
                        <Field label="所属部署" value={edit.department} onChange={(val) => setEdit({ ...edit, department: val })} testid="vd-dept-input" />
                        <Field label="担当者" value={edit.assignee} onChange={(val) => setEdit({ ...edit, assignee: val })} testid="vd-assignee-input" />
                        <div>
                            <label className="prc-label-overline block mb-1">ステータス</label>
                            <select className="prc-input" value={edit.status} onChange={(e) => setEdit({ ...edit, status: e.target.value })} data-testid="vd-status-select">
                                {STATUSES.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
                            </select>
                        </div>
                        <Field label="点検周期（ヶ月）" type="number" value={edit.inspection_cycle_months} onChange={(val) => setEdit({ ...edit, inspection_cycle_months: val ? parseInt(val) : null })} testid="vd-cycle-input" />
                    </div>
                    <div className="mt-3">
                        <label className="prc-label-overline block mb-1">備考</label>
                        <textarea className="prc-input min-h-20" value={edit.notes || ""} onChange={(e) => setEdit({ ...edit, notes: e.target.value })} data-testid="vd-notes-input" />
                    </div>
                    <div className="mt-4 flex justify-end">
                        <button onClick={save} disabled={saving} className="prc-btn-primary" data-testid="vd-save-btn">{saving ? "保存中…" : "変更を保存"}</button>
                    </div>
                </div>

                <div className="prc-card p-5">
                    <p className="prc-label-overline mb-3">添付書類</p>
                    <div className="space-y-2">
                        {(v.attachments || []).map((a) => {
                            const isPdf = String(a.data_url || "").startsWith("data:application/pdf") || /\.pdf($|\?)/i.test(String(a.name || ""));
                            const handleOpen = () => {
                                if (isPdf) {
                                    // PDFs cannot render in our <img> lightbox — open in a new tab
                                    window.open(a.data_url, "_blank", "noopener");
                                } else {
                                    setLightbox(a);
                                }
                            };
                            return (
                                <button
                                    key={a.id}
                                    type="button"
                                    onClick={handleOpen}
                                    className="w-full text-left text-sm border border-[var(--prc-border)] rounded-sm p-2 hover:border-[var(--prc-green)] hover:bg-[var(--prc-bg)] transition inline-flex items-center gap-3"
                                    data-testid={`vd-attach-item-${a.id}`}
                                >
                                    {!isPdf && (
                                        <img src={a.data_url} alt={a.name} className="w-12 h-12 rounded-sm object-cover border border-[var(--prc-border)]" />
                                    )}
                                    {isPdf && (
                                        <span className="w-12 h-12 rounded-sm bg-[var(--prc-bg)] border border-[var(--prc-border)] inline-flex items-center justify-center">
                                            <FileText size={24} />
                                        </span>
                                    )}
                                    <span className="flex-1 min-w-0">
                                        <p className="font-semibold truncate">{a.name}</p>
                                        <p className="text-xs text-[var(--prc-muted)]">{a.kind} · {fmtYmd(a.uploaded_at, String(a.uploaded_at).substring(0, 10))}</p>
                                    </span>
                                </button>
                            );
                        })}
                        {(!v.attachments || v.attachments.length === 0) && <p className="text-xs text-[var(--prc-muted)]">添付書類がありません</p>}
                    </div>
                    <div className="mt-3 space-y-2">
                        {[
                            { k: "shakenshou", l: "車検証" },
                            { k: "insurance", l: "保険証券" },
                            { k: "service", l: "整備記録" },
                            { k: "contract", l: "契約書" },
                        ].map((opt) => (
                            <label key={opt.k} className="prc-btn-ghost w-full cursor-pointer text-xs inline-flex items-center gap-2 justify-center" data-testid={`vd-attach-${opt.k}`}>
                                <UploadSimple size={16} />{opt.l}を追加
                                <input type="file" accept="image/*,application/pdf" onChange={(e) => uploadAttachment(opt.k, e.target.files?.[0])} className="hidden" />
                            </label>
                        ))}
                    </div>
                </div>
            </div>

            <VehicleQrCard vehicleId={id} plate={v.plate_number} companyName={v.company_name} />

            <div className="grid lg:grid-cols-2 gap-4">
                <div className="prc-card p-5">
                    <p className="prc-label-overline mb-3">最近の点検</p>
                    {history.length === 0 ? <p className="text-sm text-[var(--prc-muted)]">履歴なし</p> : (
                        <ul className="text-sm divide-y divide-[var(--prc-border)]" data-testid="vd-inspection-list">
                            {history.slice(0, 5).map((h) => (
                                <li key={h.id} className="py-2 flex justify-between">
                                    <div>
                                        <p className="font-semibold">{fmtYmdTime(h.started_at, String(h.started_at).substring(0, 16).replace("T", " "))}</p>
                                        <p className="text-xs text-[var(--prc-muted)]">{h.inspector_name}</p>
                                    </div>
                                    <span className={`prc-badge ${h.overall_status === "ng" ? "ng" : h.overall_status === "warn" ? "warn" : "ok"}`}>
                                        {h.overall_status || h.status}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
                <div className="prc-card p-5">
                    <p className="prc-label-overline mb-3">整備記録</p>
                    {maintenance.length === 0 ? <p className="text-sm text-[var(--prc-muted)]">記録なし</p> : (
                        <ul className="text-sm divide-y divide-[var(--prc-border)]" data-testid="vd-maintenance-list">
                            {maintenance.slice(0, 5).map((m) => (
                                <li key={m.id} className="py-2">
                                    <div className="flex justify-between">
                                        <p className="font-semibold">{fmtYmd(m.performed_at, m.performed_at)}</p>
                                        <span className="font-mono">¥{(m.cost_yen || 0).toLocaleString()}</span>
                                    </div>
                                    <p className="text-xs text-[var(--prc-muted)]">{m.kind} · {m.description}</p>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>
            <ReplacementReportDialog vehicleId={id} open={aiOpen} onClose={() => setAiOpen(false)} />

            {lightbox && (
                <div
                    className="fixed inset-0 bg-black/90 z-[80] flex items-center justify-center p-3"
                    onClick={() => setLightbox(null)}
                    data-testid="vd-attach-lightbox"
                >
                    <div className="relative max-w-5xl max-h-[95vh]" onClick={(e) => e.stopPropagation()}>
                        <button
                            type="button"
                            onClick={() => setLightbox(null)}
                            className="absolute top-2 right-2 prc-btn-ghost !p-0 h-9 w-9 inline-flex items-center justify-center z-10"
                            aria-label="閉じる"
                            data-testid="vd-attach-lightbox-close"
                        ><X size={16} /></button>
                        <a
                            href={lightbox.data_url}
                            download={lightbox.name}
                            className="absolute top-2 right-12 prc-btn-ghost !p-0 h-9 w-9 inline-flex items-center justify-center z-10"
                            aria-label="ダウンロード"
                            data-testid="vd-attach-lightbox-download"
                            onClick={(e) => e.stopPropagation()}
                        ><DownloadSimple size={16} /></a>
                        <img
                            src={lightbox.data_url}
                            alt={lightbox.name}
                            className="max-w-full max-h-[95vh] object-contain"
                            data-testid="vd-attach-lightbox-image"
                        />
                        <p className="absolute bottom-2 left-2 right-2 text-xs text-white/80 text-center font-mono pointer-events-none drop-shadow">
                            {lightbox.name}{lightbox.kind ? ` · ${lightbox.kind}` : ""}
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
}

function Field({ label, value, onChange, type = "text", testid }) {
    if (type === "date") {
        return (
            <div>
                <label className="prc-label-overline block mb-1">{label}</label>
                <DatePickerYmd value={value} onChange={onChange} testid={testid} />
            </div>
        );
    }
    return (
        <div>
            <label className="prc-label-overline block mb-1">{label}</label>
            <input
                type={type}
                className="prc-input"
                value={value ?? ""}
                onChange={(e) => onChange(e.target.value)}
                data-testid={testid}
            />
        </div>
    );
}
